import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Email Confirmation Page
 *
 * A verified App Link (Android) or Universal Link (iOS) delivers the
 * confirmation token straight to the app, so this page is only reached when
 * that did not happen: an app older than 1.1.2, link handling switched off, or
 * an in-app browser that will not hand the URL over.
 *
 * It used to forward the token onward as familywaffleapp://auth/confirm-email.
 * Any installed app can register that scheme, and resolution between competing
 * claimants is undefined on both platforms, so a hostile app could receive a
 * live confirmation token. It no longer does.
 */

const APP_STORE_URL = 'https://apps.apple.com/app/family-waffle/id6738030746';

function extractTokenFromHash(hash: string): { token?: string; code?: string } {
  // Supabase sometimes puts tokens in the hash fragment
  const params = new URLSearchParams(hash.replace('#', ''));
  return {
    token: params.get('access_token') || undefined,
    code: params.get('code') || undefined,
  };
}

// Heroicons check icon
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

// Heroicons envelope icon
const EnvelopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
  </svg>
);

export default function AuthConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation('auth');

  // Extract token/code from query params or hash fragment
  const token = searchParams.get('token');
  const code = searchParams.get('code');
  const hashParams = extractTokenFromHash(location.hash);

  const authCode = code || token || hashParams.code || hashParams.token;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative text-center max-w-md w-full">
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-2xl">
          {/* Logo */}
          <img
            src="/logo.svg"
            alt="Family Waffle"
            className="w-20 h-20 mx-auto mb-6"
          />

          {/* Status Icon */}
          <div className={`w-16 h-16 rounded-full ${authCode ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-waffle-orange to-amber-500'} flex items-center justify-center text-white mx-auto mb-4`}>
            {authCode ? <CheckIcon /> : <EnvelopeIcon />}
          </div>

          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
            {authCode ? t('confirmEmail.confirmed') : t('confirmEmail.confirmYour')}
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base text-gray-300 mb-6">
            {authCode
              ? t('confirmEmail.needsUpdate')
              : t('confirmEmail.checkEmail')}
          </p>

          {/* App Store Link */}
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-gray-400 hover:text-waffle-orange transition-colors text-sm"
          >
            {t('confirmEmail.noApp')}
          </a>

        </div>
      </div>
    </div>
  );
}
